import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const CAPABILITIES = [
  {
    name: "Websites",
    headline: "Look professional. Without overspending.",
    support:
      "Make a great first impression and give customers an easy way to do business with you.",
  },
  {
    name: "Lead generation",
    headline: "Make it easier to get the job.",
    support:
      "Let customers ask questions, send photos, request estimates, and reach out without the back-and-forth.",
  },
  {
    name: "Custom applications",
    headline: "Make the day-to-day easier.",
    support:
      "From booking and customer portals to dashboards and custom tools, build around how your business actually runs.",
  },
  {
    name: "Automation",
    headline: "Spend less time on repeat work.",
    support:
      "Connect the steps, cut down on manual follow-ups, and keep important details from slipping through.",
  },
];

export default function Manifesto() {
  return (
    // The section begins 42vh early, underneath the hero's pinned glass —
    // the headline rises into view THROUGH the clearing pane, then the hero
    // releases and the visitor is simply inside this scene.
    <section
      data-testid="manifesto-section"
      className="relative z-0 -mt-[42vh] px-5 pb-32 pt-10 md:px-16 md:pb-48 md:pt-14"
    >
      <div className="mx-auto max-w-[1500px]">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-10 text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs"
        >
          Websites that work harder.
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1.1, ease: EASE }}
          className="max-w-5xl font-display text-4xl font-medium leading-[1.02] tracking-[-0.03em] md:text-7xl"
        >
          Your website should do more than just look good.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-fog md:text-lg"
        >
          Professional doesn't have to mean expensive. We build websites and business tools
          that help customers take the next step—and take some of the work off your plate.
        </motion.p>

        <ul className="mt-24 md:mt-36" data-testid="capability-list">
          {CAPABILITIES.map((cap, i) => (
            <motion.li
              key={cap.name}
              initial={{ opacity: 0, y: 70 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 1, ease: EASE, delay: 0.05 * i }}
              className="group grid gap-4 border-t border-white/10 py-10 last:border-b md:grid-cols-12 md:items-end md:gap-12 md:py-14"
            >
              <span className="font-body text-xs tracking-[0.3em] text-fog md:col-span-1">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="md:col-span-7">
                <p className="text-[10px] font-medium uppercase tracking-[0.4em] text-fog md:text-xs">
                  {cap.name}
                </p>
                <h3 className="mt-3 font-display text-3xl font-medium leading-[1.05] tracking-[-0.02em] transition-transform duration-700 ease-cinematic md:text-[3.4vw] md:group-hover:translate-x-4">
                  {cap.headline}
                </h3>
              </div>
              <p className="max-w-md text-sm leading-relaxed text-fog md:col-span-4 md:text-base">
                {cap.support}
              </p>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
